import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import apiService from '../../services/apiService';
import { Button } from "../../components/ui/button";
import SimpleDateRangePicker from '../../components/SimpleDateRangePicker';


interface PeriodOptions {
  period: "this-week";
  from?: Date;
  to?: Date;
}

const StatsTab = () => {
  const [filters, setFilters] = useState<{
    period?: string;
    periodFrom?: Date;
    periodTo?: Date;
    performance?: string;
    performanceFrom?: Date;
    performanceTo?: Date;
  }>({
    period: 'overall',
    performance: 'overall',
  });
  const [activityPeriod, setActivityPeriod] = useState('this-week');
  const [rmPeriod, setRmPeriod] = useState('today');
  const [selectedRM, setSelectedRM] = useState<number | null>(null);
  const [Period, setPeriod] = useState<PeriodOptions>();
  const [showComparison, setShowComparison] = useState(false);
  const [dashboardData, setDashboardData] = useState({});
  const [showDateRange, setShowDateRange] = useState(false);
  const [customRange, setCustomRange] = useState<{ from: Date | null, to: Date | null }>({ from: null, to: null });
  const [performanceCustomRange, setPerformanceCustomRange] = useState<{ from: Date | null, to: Date | null }>({ from: null, to: null });
  const [activityTrends, setActivityTrends] = useState<any[]>([]);
  const [rmDatas, setRmData] = useState([]);
  const [uniqueRmNames, setUniqueRmNames] = useState<{ id: number, name: string }[]>([]);


  const handlePeriodChange = (value: string) => {
    setFilters(prev => {
      const updated = { ...prev, period: value };
      if (value === 'custom') {
        setShowDateRange(true);
      } else {
        setShowDateRange(false);
        fetchStats(updated);
      }
      return updated;
    });
  };

  const fetchRMPerformance = async () => {
    const response = await apiService.post('/leads/rm-performance', {
      period: rmPeriod,
      rmId: selectedRM,
      comparison: showComparison
    });
    setRmData(response.data || []);
  };

  useEffect(() => {
    const getRMListData = async () => {
      try {
        const response: { data: { id: number, name: string }[] } = await apiService.get("/users/rm-list");
        setUniqueRmNames(response.data);
        if (response.data && response.data.length > 0) {
          setSelectedRM(response.data[0].id); // Set first RM's ID
        }
      } catch (e) {
        // handle error
      }
    };
    getRMListData();
  }, []);

  useEffect(() => {
    if (selectedRM !== null) {
      fetchActivityTrends(Period || { period: "this-week" });
    }
  }, [selectedRM]);

  const handlePerformanceChange = (value: string) => {
    setFilters(prev => {
      const updated = { ...prev, performance: value };
      if (value === 'custom') {
        setShowDateRange(true);
      } else {
        setShowDateRange(false);
        fetchStats(updated);
      }
      return updated;
    });
  };

  const handleApplyCustomRange = () => {
    setShowDateRange(false);
    setFilters(prev => {
      const updated = {
        ...prev,
        period: prev.period === 'custom' ? 'custom' : prev.period,
        periodFrom: customRange.from,
        periodTo: customRange.to,
        // add performanceFrom/performanceTo if needed
      };
      fetchStats(updated);
      return updated;
    });
  };

  const handleApplyPerformanceCustomRange = () => {
    setShowDateRange(false);
    setFilters(prev => {
      const updated = {
        ...prev,
        performance: 'custom',
        performanceFrom: performanceCustomRange.from,
        performanceTo: performanceCustomRange.to,
      };
      fetchStats(updated);
      return updated;
    });
  };

  const handleActivityPeriodChange = (value: string) => {
    setActivityPeriod(value);
    if (value === 'custom') {
      setShowDateRange(true);
    } else {
      setShowDateRange(false);
      fetchActivityTrends({ period: value });
    }
  };


  useEffect(() => {
    fetchStats(filters);
  }, []);

  const fetchStats = async (params: any) => {
    try {
      // Convert Date objects to ISO strings
      const apiParams = { ...params };
      if (apiParams.periodFrom) apiParams.periodFrom = apiParams.periodFrom.toISOString();
      if (apiParams.periodTo) apiParams.periodTo = apiParams.periodTo.toISOString();
      if (apiParams.performanceFrom) apiParams.performanceFrom = apiParams.performanceFrom.toISOString();
      if (apiParams.performanceTo) apiParams.performanceTo = apiParams.performanceTo.toISOString();

      const response = await apiService.post(`/leads/getDashboardStats`, apiParams);
      if (response && response.data) {
        setDashboardData(response.data);
      } else {
        setDashboardData({});
        console.error('Unexpected response format:', response);
      }
    } catch (error) {
      setDashboardData({});
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const fetchActivityTrends = async (options: PeriodOptions) => {
    const { period, from, to } = options;
    try {
      const params: any = { period };
      if (period === 'custom' && from && to) {
        params.periodFrom = from.toISOString();
        params.periodTo = to.toISOString();
      }
      setPeriod(params)
      params.rmId = selectedRM || "";



      const response = await apiService.post('/leads/getActivityTrends', params);
      if (response && response.data) {
        setActivityTrends(response.data); // Adjust if your API returns { data: [...] }
      } else {
        setActivityTrends([]);
        console.error('Unexpected response format:', response);
      }
    } catch (error) {
      setActivityTrends([]);
      console.error('Failed to fetch activity trends:', error);
    }
  };

  useEffect(() => {
    fetchStats(filters);
  }, [filters.period, filters.performance]);

  useEffect(() => {
    fetchActivityTrends({ period: activityPeriod });
  }, []);

  const rmData = rmDatas;

  const getComparisonLabel = () => {
    if (rmPeriod === 'today') return 'Yesterday';
    if (rmPeriod === 'yesterday') return 'Today';
    return 'Previous Week';
  };

  return (
    <div className="space-y-6 px-5">
      {/* Deals by Stage */}
      <Card className="border border-[#d9cba6]/30 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-[#d9cba6]/10 to-[#e5d7b3]/10 border-b border-[#d9cba6]/20">
          <CardTitle className="text-[#8b7355] font-semibold">Deals by Stage</CardTitle>
          <div className='flex gap-8'>

            <Select value={filters.period} onValueChange={handlePeriodChange}>
              <SelectTrigger className="w-40 border-[#d9cba6]/50 focus:border-[#d9cba6]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className='bg-white'>
                <SelectItem value="overall">Overall</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="this-week">This Week</SelectItem>
                <SelectItem value="previous-week">Previous Week</SelectItem>
                <SelectItem value="this-month">This Month</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
            {filters.period === 'custom' && (
              <div className="flex items-center gap-2 ">
                <SimpleDateRangePicker
                  dateRange={customRange}
                  onDateRangeChange={(range) => setCustomRange(range as any)}
                />
                <Button
                  onClick={handleApplyCustomRange}
                  disabled={!customRange.from || !customRange.to}
                >
                  Apply
                </Button>
              </div>
            )}

          </div>

        </CardHeader>
        <CardContent className="pt-6">
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dashboardData?.stats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d9cba6" opacity={0.3} />
                <XAxis
                  dataKey="status"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  fontSize={12}
                  stroke="#8b7355"
                />
                <YAxis stroke="#8b7355" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #d9cba6',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar
                  dataKey="count"
                  fill="#d9cba6"
                  radius={[4, 4, 0, 0]}
                  stroke="#c9b896"
                  strokeWidth={1}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Activity Trends */}
      <Card className="border border-[#d9cba6]/30 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-[#d9cba6]/10 to-[#e5d7b3]/10 border-b border-[#d9cba6]/20">
          <CardTitle className="text-[#8b7355] font-semibold">Activity Trends</CardTitle>
          <div className='flex gap-4'>
            <Select value={selectedRM ? String(selectedRM) : undefined} onValueChange={val => setSelectedRM(Number(val))}>
              <SelectTrigger>
                <SelectValue placeholder="Select RM" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem key={0} value={String(0)}>
                  All
                </SelectItem>
                {uniqueRmNames.map(rm => (
                  <SelectItem key={rm.id} value={String(rm.id)}>
                    {rm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={activityPeriod} onValueChange={handleActivityPeriodChange}>
              <SelectTrigger className="w-40 border-[#d9cba6]/50 focus:border-[#d9cba6]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className='bg-white w-fit'>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="this-week">This Week</SelectItem>
                <SelectItem value="this-month">This Month</SelectItem>
              </SelectContent>
            </Select>
          </div>

        </CardHeader>
        <CardContent className="pt-6">
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={activityTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d9cba6" opacity={0.3} />
                <XAxis dataKey="time" stroke="#8b7355" />
                <YAxis stroke="#8b7355" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #d9cba6',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="newCalls"
                  stroke="#d9cba6"
                  strokeWidth={3}
                  dot={{ fill: '#d9cba6', stroke: '#c9b896', strokeWidth: 2, r: 4 }}
                  name="New Calls"
                />
                <Line
                  type="monotone"
                  dataKey="followUpCalls"
                  stroke="#8b7355"
                  strokeWidth={3}
                  dot={{ fill: '#8b7355', stroke: '#6b5c44', strokeWidth: 2, r: 4 }}
                  name="Follow-up Calls"
                />
                <Line
                  type="monotone"
                  dataKey="totalCalls"
                  stroke="#c9b896"
                  strokeWidth={3}
                  dot={{ fill: '#c9b896', stroke: '#b8a585', strokeWidth: 2, r: 4 }}
                  name="Total Calls"
                />
                <Line
                  type="monotone"
                  dataKey="meetingSetup"
                  stroke="#a89475"
                  strokeWidth={3}
                  dot={{ fill: '#a89475', stroke: '#978364', strokeWidth: 2, r: 4 }}
                  name="Meeting Setup"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Enhanced RM Performance */}


      {/* RM Performance Overview Table */}
      <Card className="border border-[#d9cba6]/30 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-[#d9cba6]/10 to-[#e5d7b3]/10 border-b border-[#d9cba6]/20">
          <CardTitle className="text-[#8b7355] font-semibold">RM Performance Overview</CardTitle>
          <div className='flex gap-8'>

            <Select value={filters.performance} onValueChange={handlePerformanceChange}>
              <SelectTrigger className="w-40 border-[#d9cba6]/50 focus:border-[#d9cba6]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className='bg-white'>
                <SelectItem value="overall">Overall</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="this-week">This Week</SelectItem>
                <SelectItem value="previous-week">Previous Week</SelectItem>
                <SelectItem value="this-month">This Month</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
            {filters.performance === 'custom' && (
              <div className="flex items-center gap-2 ">
                <SimpleDateRangePicker
                  dateRange={performanceCustomRange}
                  onDateRangeChange={(range) => setPerformanceCustomRange(range as any)}
                />
                <Button
                  onClick={handleApplyPerformanceCustomRange}
                  disabled={!performanceCustomRange.from || !performanceCustomRange.to}
                >
                  Apply
                </Button>
              </div>
            )}

          </div>

        </CardHeader>


        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-[#d9cba6]/20 to-[#e5d7b3]/20">
                <tr>
                  <th className="text-left p-4 font-medium text-[#8b7355]">RM Name</th>
                  <th className="text-left p-4 font-medium text-[#8b7355]">Total Call</th>
                  <th className="text-left p-4 font-medium text-[#8b7355]">New Call</th>
                  <th className="text-left p-4 font-medium text-[#8b7355]">Follow-up Calls</th>
                  <th className="text-left p-4 font-medium text-[#8b7355]">Meetings Setup</th>
                  <th className="text-left p-4 font-medium text-[#8b7355]">Deals Won</th>
                  <th className="text-left p-4 font-medium text-[#8b7355]">Conversion Rate</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.rmPerformance && Array.isArray(dashboardData.rmPerformance) && dashboardData.rmPerformance.length > 0 ? (
                  dashboardData.rmPerformance.map((rm, index) => (
                    <tr key={rm.name} className={`border-b hover:bg-[#d9cba6]/5 transition-colors ${index % 2 === 0 ? 'bg-[#d9cba6]/5' : ''}`}>
                      <td className="p-4 font-medium text-gray-900">{rm.name}</td>

                      {/* Total Calls = newcalls + followupcalls */}
                      <td className="p-4">
                        {(rm?.newcalls || 0) + (rm?.followupcalls || 0)}
                      </td>

                      {/* New Calls */}
                      <td className="p-4">{rm?.newcalls > 0 ? rm.newcalls : 0}</td>

                      {/* Followup Calls */}
                      <td className="p-4">{rm?.followupcalls > 0 ? rm.followupcalls : 0}</td>

                      {/* Meeting Setup */}
                      <td className="p-4">{rm?.meetingsetup > 0 ? rm.meetingsetup : 0}</td>

                      {/* Won */}
                      <td className="p-4">{rm?.won > 0 ? rm.won : 0}</td>

                      {/* Conversion Rate = (meetingsetup / totalCalls) * 100 */}
                      <td className="p-4">
                        {((rm?.meetingsetup || 0) > 0 && ((rm?.newcalls || 0) + (rm?.followupcalls || 0)) > 0)
                          ? (((rm?.meetingsetup || 0) / ((rm?.newcalls || 0) + (rm?.followupcalls || 0))) * 100).toFixed(2)
                          : '0.00'}%
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="p-4 text-center text-gray-500">No Data</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default StatsTab;
